import React, {
  useContext, useState, useMemo, useEffect, useRef
} from 'react';
import {
  bool, shape, func, string
} from 'prop-types';
import classNames from 'classnames';
import { Cart, Compare, TrashCan } from '@one-thd/sui-icons';
import { Button, Typography } from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { AddTo } from '@thd-olt-component-react/add-to';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { ListSettings, EmailList } from '@thd-olt-component-react/list-forms';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ProductListContext } from '../ProductListContext';
import {
  getAtcEnabledProducts,
  getProductsFromGroups,
  isPureIntentItem,
  getSelectedCatalogProducts,
  isProductInGroup
} from '../productListHelper';
import { ActionBarButton } from '../action-bar-button/ActionBarButton';
import { TOO_MANY_PRODUCTS_MESSAGE, QUANTITY_LIMIT, NO_ITEMS_SELECTED_ATC_MESSAGE } from './constants';
import { LoadingDrawer } from '../loading-drawer/LoadingDrawer';
import { AddToMobile } from './AddToMobile';
import { DeleteModal } from '../delete-modal/DeleteModal';
// TODO: figure out where to add an alert for issues.

export const ActionBar = ({
  showActionBar,
  sticky,
  selectedItemsByGroup,
  onGroupAccordionSelectionChange,
  errorSelectionEmpty,
  updateErrorSelectionEmpty
}) => {
  const { channel } = useContext(ExperienceContext);
  const store = useStore({ varnish: true });
  const isMobile = channel === 'mobile';
  const {
    isSharedList,
    list,
    onProductSelectionChange,
    productsDisplayed,
    removeListItems,
    setProductsDisplayed,
    // Product data management for ATC functionality
    productData,
    fetchProducts,
    loadedItemIds
  } = useContext(ProductListContext);

  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addToType, setAddToType] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Extract all products from groups using helper function
  const products = useMemo(() => getProductsFromGroups(list), [list]);

  // Create filtered version without pure intents for catalog operations
  const catalogProducts = useMemo(() => {
    return products.filter((product) => !isPureIntentItem(product));
  }, [products]);

  const { isB2BCustomer = false } = useThdCustomer();

  const buttonRef = useRef(null);

  // Extract selected group IDs and item IDs from GroupAccordion selections
  const groupAccordionSelections = useMemo(() => {
    if (!selectedItemsByGroup) {
      return { groupIds: [], itemIds: [] };
    }

    const groupIds = [];
    const itemIds = [];

    Object.keys(selectedItemsByGroup).forEach((groupId) => {
      const group = selectedItemsByGroup[groupId];

      // If entire group is selected (and not indeterminate), add group ID
      if (group?.isSelected && !group?.isIndeterminate) {
        groupIds.push(groupId);
      }

      // If individual items are selected within the group, add item IDs
      if (group?.items && group.items.length > 0) {
        itemIds.push(...group.items);
      }
    });

    return { groupIds, itemIds };
  }, [selectedItemsByGroup]);

  // Memoized collection of all selected catalog products (used by multiple calculations)
  const selectedCatalogProducts = useMemo(() => {
    return getSelectedCatalogProducts({ groupAccordionSelections, catalogProducts });
  }, [groupAccordionSelections, catalogProducts]);

  useEffect(() => {
    const { groupIds, itemIds } = groupAccordionSelections;
    const totalSelectedItems = itemIds.length + groupIds.reduce((count, groupId) => {
      // Count all items in selected groups
      const groupProducts = products.filter((product) => isProductInGroup(product, groupId));
      return count + groupProducts.length;
    }, 0);
    if (totalSelectedItems === 0 && isAllSelected) {
      // Trigger deselect-all analytics when transitioning from all-selected to not-all-selected
      window.LIFE_CYCLE_EVENT_BUS.trigger('product-list.deselect-all', {});
    }

    if (products.length && totalSelectedItems === products.length) {
      setIsAllSelected(true);
    } else if (
      (totalSelectedItems === 0 || totalSelectedItems !== products.length)
      && isAllSelected
    ) {
      setIsAllSelected(false);
    }
  }, [groupAccordionSelections, products, isAllSelected]);

  let baseUrl = '';
  if (typeof window !== 'undefined') {
    baseUrl = window.origin;
  }

  // Function to get product data with quantity (following ProjectATC pattern)
  const getProductDataWithQuantity = (items) => {
    return items.map((item) => {
      const atcProduct = productsDisplayed?.find((product) => product.itemId === item.itemId) || {};
      return {
        ...atcProduct,
        quantity: item.quantity
      };
    }).filter((item) => item?.itemId);
  };

  const cartReqParams = useMemo(() => {
    if (selectedCatalogProducts.length > 0) {
      // Get product data with quantity
      const selectedProducts = getProductDataWithQuantity(selectedCatalogProducts);

      const atcEnabledProducts = getAtcEnabledProducts({
        products: selectedProducts,
        store
      });

      return atcEnabledProducts;
    }
    return [];
  }, [selectedCatalogProducts, productsDisplayed, store]);

  // Calculate selected item count from GroupAccordion selections
  const selectedItemCount = useMemo(() => {
    const { groupIds, itemIds } = groupAccordionSelections;
    return itemIds.length + groupIds.reduce((count, groupId) => {
      // Count all items in selected groups (excluding items already counted individually)
      const group = selectedItemsByGroup[groupId];
      if (group?.isSelected && !group?.isIndeterminate) {
        // Find all products in this group to count them
        const groupProducts = products.filter((product) => isProductInGroup(product, groupId));
        return count + groupProducts.length;
      }
      return count;
    }, 0);
  }, [groupAccordionSelections, selectedItemsByGroup, products]);

  const isComparable = useMemo(() => {
    // Only comparable if we have 2-4 catalog items
    return selectedCatalogProducts.length > 1 && selectedCatalogProducts.length < 5;
  }, [selectedCatalogProducts]);

  const iconsContainer = classNames('sui-flex', {
    'sui-gap-2 sui-pr-0 sui-pl-0 sui-pb-4 sui-pt-2': isMobile,
    'sui-gap-2 sui-p-6 sui-pr-0': !isMobile
  });

  const stickyIconsContainer = classNames(
    'sui-gap-2', 'sui-flex'
  );

  const onCompareClick = () => {
    // Trigger analytics for compare
    global.LIFE_CYCLE_EVENT_BUS.trigger('product-list.compare', {});

    const catalogItemIds = selectedCatalogProducts.map((item) => item.itemId);

    if (catalogItemIds.length > 1 && catalogItemIds.length < 5) {
      const queryparams = catalogItemIds
        .map((item, idx) => `item${idx + 1}=${item}`)
        .join('&');
      window.location.href = `${baseUrl}/compare?${queryparams}`;
    }
  };

  const onATCSuccess = (obj) => {
    const analyticsItemsData = selectedCatalogProducts.map((item) => {
      const displayedProducts = productsDisplayed || productData || [];
      return {
        itemId: item.itemId,
        index: displayedProducts.findIndex((pd) => pd.itemId === item.itemId)
      };
    });

    LIFE_CYCLE_EVENT_BUS.trigger('product-list.atc', { products: analyticsItemsData });
  };

  const fetchedItems = useMemo(() => {
    return selectedCatalogProducts.map((item) => {
      const newQuantity = item.quantity > QUANTITY_LIMIT ? QUANTITY_LIMIT : item.quantity;

      const fetchedItem = productsDisplayed?.find((product) => product.itemId === item.itemId)
        || productData?.find((product) => product.itemId === item.itemId);

      if (!fetchedItem) return null;

      return {
        ...fetchedItem,
        quantity: newQuantity
      };
    }).filter(Boolean);
  }, [selectedCatalogProducts, productsDisplayed, productData]);

  const arrayOfSelectedItems = useMemo(() => {
    return selectedCatalogProducts.map((item) => {
      const newQuantity = item.quantity > QUANTITY_LIMIT ? QUANTITY_LIMIT : item.quantity;

      return {
        itemId: item.itemId,
        quantity: newQuantity,
        itemType: 'catalog'
      };
    });
  }, [selectedCatalogProducts]);
  const addToProducts = useMemo(() => {
    return fetchedItems?.map((fetchedItem) => {
      if (!fetchedItem) return null;
      return {
        itemId: fetchedItem?.itemId,
        price: fetchedItem?.pricing?.value || null,
        productData: { info: fetchedItem?.info, identifiers: fetchedItem?.identifiers, pricing: fetchedItem?.pricing },
        quantity: fetchedItem?.quantity,
        sku: {
          storeSkuNumber: fetchedItem?.identifiers?.storeSkuNumber || null,
          omsThdSku: fetchedItem?.identifiers?.omsThdSku || null,
          specialOrderSku: fetchedItem?.identifiers?.specialOrderSku || null
        },
        itemType: 'catalog'
      };
    }).filter(Boolean);
  }, [fetchedItems]);
  const onLoadingSuccess = () => {
    setIsLoading(false);

    if (addToType === 'Cart') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('cart.add-to-cart', {
        cartReqParams,
        cartOptions: {
          channel,
          host: typeof window !== 'undefined' ? window.origin : 'https://www.homedepot.com',
          misship: false,
          paypal: false
        },
        onSuccess: onATCSuccess
      });
    }

    if (addToType === 'Quote') {
      buttonRef.current.click();
    }

    if (addToType === '...') {
      buttonRef.current.firstChild.click();
    }

    setAddToType('');
  };

  const needToLoadProducts = useMemo(() => {
    // Check if catalog items are loaded in productsDisplayed
    const catalogItemIds = selectedCatalogProducts.map((item) => item.itemId);
    const displayedProducts = productsDisplayed || productData || [];
    const displayedItemIds = displayedProducts.map((item) => item?.itemId);
    const unloadedItems = catalogItemIds.filter((id) => !displayedItemIds.includes(id));

    return unloadedItems.length > 0;
  }, [selectedCatalogProducts, productsDisplayed, productData]);

  const shouldShowLoadingScreen = isLoading && needToLoadProducts;

  // Handle loading completion in useEffect to avoid setState during render
  useEffect(() => {
    if (isLoading && !needToLoadProducts) {
      setIsLoading(false);
      onLoadingSuccess();
    }
  }, [isLoading, needToLoadProducts]);

  const handleAddToClick = (onClickFunc, type) => {
    if (needToLoadProducts) {
      setAddToType(type);
      setIsLoading(true);
    } else {
      onClickFunc();
    }
  };

  const handleProductCountCheck = (onClickFunc) => {
    if (selectedItemCount > 89) {
      // TODO: Uncomment the below line when the alert is ready
      // setShowProductSelectionAlert(true);
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.errorAnalytics', {
        error: [{
          type: 'advisory',
          displayType: 'inline',
          pageSection: 'list details',
          message: TOO_MANY_PRODUCTS_MESSAGE,
          code: 'n/a',
        }]
      });
    }

    handleAddToClick(onClickFunc, 'Quote');
  };

  const updateLoadedProductsData = (newProductData, productId) => {
    const isArray = Array.isArray(newProductData);
    if (isArray) {
      setProductsDisplayed((prevData) => {
        // Filter out products that are already loaded to prevent duplicates
        const existingItemIds = new Set(prevData.map((item) => item.itemId));
        const newProducts = newProductData.filter((product) => !existingItemIds.has(product.itemId));

        // Only update if there are new products to add
        if (newProducts.length > 0) {
          return [...prevData, ...newProducts];
        }

        return prevData;
      });
    }
  };

  // Check if any groups or items are selected that contain actual catalog items
  const hasGroupAccordionSelections = useMemo(() => {
    // Only enable buttons if there are actual catalog items selected
    return selectedCatalogProducts.length > 0;
  }, [selectedCatalogProducts]);

  const handleATCClick = (onClick) => {
    // Check if any items are selected first (before ATC-filtering)
    if (!selectedCatalogProducts?.length) {
      updateErrorSelectionEmpty(NO_ITEMS_SELECTED_ATC_MESSAGE);
      return;
    }
    if (errorSelectionEmpty) updateErrorSelectionEmpty(null);
    // Let AddToCart handle items without fulfillment options appropriately
    handleAddToClick(onClick, 'Cart');
  };

  // Separate logic for delete button - can delete empty groups (except Default Group)
  const hasDeleteableSelections = useMemo(() => {
    const { groupIds, itemIds } = groupAccordionSelections;

    // Can delete if there are individual items selected
    if (itemIds.length > 0) {
      return true;
    }

    // Can delete if there are groups selected (with or without items), except Default Group
    if (groupIds.length > 0) {
      // Check if any selected group is NOT the Default Group
      const hasNonDefaultGroup = groupIds.some((groupId) => groupId !== '__ungrouped__' && groupId !== null
      );

      if (hasNonDefaultGroup) {
        return true;
      }

      // If only Default Group is selected, check if it has items
      if (groupIds.includes('__ungrouped__') || groupIds.includes(null)) {
        const defaultGroupProducts = (products).filter((product) => (
          !product.groupId || product.groupId === '__ungrouped__'
        ));
        return defaultGroupProducts.length > 0;
      }
    }

    return false;
  }, [groupAccordionSelections, products]);

  const noSelectedItems = !hasGroupAccordionSelections;
  const noDeleteableItems = !hasDeleteableSelections;

  if (!showActionBar) return null;

  return (
    <div
      data-testid={sticky ? 'sticky-action-bar' : 'list-action-bar'}
      className="sui-flex sui-flex-row sui-gap-2"
    >
      <div className={sticky ? stickyIconsContainer : iconsContainer}>
        {!isMobile && (
          <>
            <div>
              {isB2BCustomer && (
                <AddTo
                  addToType="Quote"
                  CustomAddToActionElement={({ onClick }) => (
                    <Button
                      aria-label="Add To Quote"
                      disabled={noSelectedItems}
                      onClick={() => handleProductCountCheck(onClick)}
                      variant="ghost"
                      ref={buttonRef}
                    >
                      Add to Quote
                    </Button>
                  )}
                  loadedProductsData={productsDisplayed}
                  products={arrayOfSelectedItems}
                  updateLoadedProductsData={updateLoadedProductsData}
                />
              )}
              <AddTo
                addToType="List"
                products={arrayOfSelectedItems}
                CustomAddToActionElement={({ onClick }) => (
                  <Button
                    aria-label="Add To List"
                    disabled={noSelectedItems}
                    onClick={onClick}
                    variant="ghost"
                  >
                    Add to List
                  </Button>
                )}
              />
            </div>
            <div>
              <AddTo
                addToType="Project"
                addToFallbackType={null}
                CustomAddToActionElement={({ onClick }) => (
                  <ActionBarButton
                    ariaLabel="Add To Project"
                    variant="ghost"
                    onClick={onClick}
                    disabled={noSelectedItems}
                    text="Add to Project"
                  />
                )}
                products={addToProducts}
              />
            </div>
          </>
        )}
        {isMobile && (
          <div>
            <AddToMobile
              addToProducts={addToProducts}
              handleAddToClick={handleAddToClick}
              needToLoadProducts={needToLoadProducts}
              noSelectedItems={noSelectedItems}
              ref={buttonRef}
              selectedItemCount={selectedItemCount}
            />
          </div>
        )}
        <div>
          <ActionBarButton
            ariaLabel="Compare"
            Icon={Compare}
            variant="tertiary"
            onClick={onCompareClick}
            disabled={!isComparable}
            text="Compare"
          />
        </div>
        <AddToCart
          cartReqParams={cartReqParams}
          cartOptions={{
            channel,
            host: typeof window !== 'undefined' ? window.origin : 'https://www.homedepot.com',
            misship: false,
            paypal: false
          }}
          onSuccess={onATCSuccess}
          customATCButtonRenderFunction={({ onClick }) => (
            <ActionBarButton
              ariaLabel="Add to Cart"
              Icon={Cart}
              text="Add to Cart"
              onClick={() => handleATCClick(onClick)}
              variant="tertiary"
            />
          )}
        />
        {!isSharedList && (
          <div>
            <ActionBarButton
              ariaLabel="Delete"
              Icon={TrashCan}
              variant="tertiary"
              onClick={() => setShowDeleteModal(true)}
              disabled={noDeleteableItems}
              text="Remove"
            />
          </div>
        )}
      </div>
      <LoadingDrawer
        arrayOfSelectedItems={arrayOfSelectedItems}
        onClose={() => setIsLoading(false)}
        onLoadingSuccess={onLoadingSuccess}
        open={shouldShowLoadingScreen}
        title={`Add To ${addToType}`}
      />
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        selectedItems={groupAccordionSelections.itemIds}
        selectedGroups={groupAccordionSelections.groupIds}
      />
    </div>
  );
};

ActionBar.displayName = 'ProductList-ActionBar';
ActionBar.dataModel = extend(
  AddTo,
  AddToCart,
  EmailList,
  ListSettings
);

ActionBar.propTypes = {
  showActionBar: bool,
  sticky: bool,
  selectedItemsByGroup: shape({}),
  onGroupAccordionSelectionChange: func,
  errorSelectionEmpty: string,
  updateErrorSelectionEmpty: func
};

ActionBar.defaultProps = {
  showActionBar: true,
  sticky: false,
  selectedItemsByGroup: {},
  onGroupAccordionSelectionChange: null,
  errorSelectionEmpty: null,
  updateErrorSelectionEmpty: () => {}
};
